/** @jsxImportSource @emotion/react */

import { CHECK_CARD_TYPE, SUB_LAYER_ROWS, VIEW } from "../../../../actions/Layers/constants";
import useConfig from "../../../../actions/Tenants/config/configHook";
import FallbackMaxAgeConfigForm from "./FallbackMaxAgeConfigForm";
import FieldConfigForm, { SingleFieldForm } from "./FieldConfigForm";
import SubConfigForm from "./SubConfigForm";
import { yesOrNoRadio } from "./util";

const defaultConfig = {
    title: "",
    field: {
        field_id: null,
        fieldname_getter: "",
        label: "",
    },
    view: VIEW.SELF,
    filter_field: "",
    filter_location: undefined,
    rows: SUB_LAYER_ROWS.CHILDREN,
    only_checks_for_this_layer: false,
    card_type: CHECK_CARD_TYPE.DATE,
};

// * The settings of a children table (show_children, and each tab of show_tabs)
export const SUB_LAYER_ATTRIBUTES = ["view", "fields", "title", "filter_field", "filter_location", "fallback_check_types", "fallback_check_max_age_days", "rows", "only_checks_for_this_layer"];

// * Form of one children table / layer component. Also used for each item of show_tabs (SubLayerTabsConfigForm).
export const getLayerComponentForm = (object, setObject, checkLocationOptions) => [
    {
        label: "Title",
        description: "Title of the component, leave blank for default title.",
        type: "text",
        name: "title",
    },
    {
        label: "Field",
        type: "element",
        name: "field",
        el: <SingleFieldForm
            title="Field"
            setField={(field) => setObject({ ...object, field })}
            field={object.field} />,
    },
    {
        type: "element",
        name: "fields",
        el: <FieldConfigForm
            fields={object.fields || []}
            description="What fields to display"
            useDisplayFilter={["fruit_types", "exclude_fruit_types"]}
            name="fields"
            title="Fields"
            setArray={(fields) => setObject({ ...object, fields })} />
    },
    {
        label: "View",
        description: "Which data will be used? SELF is only data on this layer, EXTRAPOLATE is data from parent layers, CHILDREN is data from child layers",
        type: "single-select",
        name: "view",
        options: Object.keys(VIEW).map((key) => ({ label: key, value: VIEW[key] })),
        onchange_callback: ({ setValue, field, value }) => {
            if (value !== VIEW.EXTRAPOLATE) {
                // * filter_field only applies to extrapolated data; filter_location also filters children's checks
                setObject({ ...object, [field]: value, filter_field: undefined });
            } else {
                setValue(field, value);
            }
        }
    },
    {
        label: "Fiter field",
        description: "When you extrapolate data from sibling/parent layers, you can filter the data by this field. For example, if you want to display only data from the same size, you can set this field to 'size' and only data of the same size will be shown.",
        type: "text",
        name: "filter_field",
    },
    {
        label: "Filter check location",
        type: "single-select",
        name: "filter_location",
        multi: true,
        return_array: true,
        description: "Only use checks of other layers (children, parents or siblings) done at these check locations. For example, set it to 'outbound' to show the outbound checks of the pallets on a sales order. Leave empty to use checks of every location.",
        options: checkLocationOptions
    },
    {
        label: "Fallback check locations",
        type: "single-select",
        name: "fallback_check_types",
        multi: true,
        return_array: true,
        description: "When a child has no check at the filter check locations, use its checks at these locations instead, for example 'intake'. When this layer has a facility, only checks done at that facility count.",
        options: checkLocationOptions
    },
    {
        type: "element",
        name: "fallback_check_max_age_days",
        el: <FallbackMaxAgeConfigForm
            value={object.fallback_check_max_age_days}
            setValue={(fallback_check_max_age_days) => setObject({ ...object, fallback_check_max_age_days })} />
    },
    {
        label: "Rows",
        type: "single-select",
        name: "rows",
        description: "CHILDREN shows one row per child with its latest check. CHECKS shows one row per check of a child: the check's own values are kept, so a row does not change when the child changes later.",
        options: Object.keys(SUB_LAYER_ROWS).map((key) => ({ label: key, value: SUB_LAYER_ROWS[key] }))
    },
    {
        label: "Only checks for this layer",
        name: "only_checks_for_this_layer",
        description: "Only use checks done for this layer, not for another parent of the same child (fallback checks are not filtered). A check stores its parents when it is created; older checks without stored parents still show for every parent of their child.",
        ...yesOrNoRadio
    },
    {
        label: "Card type",
        type: "single-select",
        name: "card_type",
        description: "Which card type to show in the calender overview",
        options: Object.keys(CHECK_CARD_TYPE).map((key) => ({ label: key, value: CHECK_CARD_TYPE[key] }))
    },
];

export const useCheckLocationOptions = () => useConfig().check_locations.map(({ value, text }) => ({ value, label: `${text} (${value})` }));

export default function LayerComponentConfigForm({ title, description, subconfig, setSubconfig, name, attributes }) {
    const checkLocationOptions = useCheckLocationOptions();

    const filteredDefaultConfig = attributes.reduce((config, attr) => {
        if (Object.prototype.hasOwnProperty.call(defaultConfig, attr)) {
            config[attr] = defaultConfig[attr];
        }
        return config;
    }, {});

    const getForm = (object) => {
        if (!object) {
            return [];
        }
        return getLayerComponentForm(object, setSubconfig, checkLocationOptions).filter((i) => attributes.includes(i.name));
    };


    return <SubConfigForm
        name={name}
        title={title}
        description={description}
        subconfig={subconfig}
        setSubconfig={setSubconfig}
        defaultSubconfig={filteredDefaultConfig}
        getForm={getForm}
    />;
}


